/**
 * Formulario de contacto: validación en el cliente + envío por mail.
 *
 * El envío va a Web3Forms (ver FORM en js/config.js), que reenvía los datos
 * como mail a la casilla de la agencia. El cuerpo del mail se arma acá:
 * FIELDS define qué etiqueta lleva cada dato y en qué orden aparece, así el
 * mail que llega se lee como una ficha y no como un volcado de campos.
 *
 * La validación nativa del navegador sigue siendo la red de seguridad:
 * acá solo mejoramos los mensajes y el momento en que aparecen.
 */

import { CONTACT, FORM } from '../config.js';

const MESSAGES = {
  valueMissing: 'Completá este campo.',
  typeMismatch: {
    email: 'Escribí un email válido (ej: nombre@empresa.com).',
    url: 'Escribí una URL completa (ej: https://tutienda.com).',
    tel: 'Escribí un número de teléfono válido.',
  },
  patternMismatch: 'El formato no es correcto.',
  default: 'Revisá este dato.',
};

function messageFor(field) {
  const v = field.validity;
  if (v.valueMissing) return MESSAGES.valueMissing;
  if (v.typeMismatch) return MESSAGES.typeMismatch[field.type] || MESSAGES.default;
  if (v.patternMismatch) return MESSAGES.patternMismatch;
  return field.validationMessage || MESSAGES.default;
}

function showError(field, message) {
  const row = field.closest('.form__row');
  if (!row) return;
  const slot = row.querySelector('[data-error]');
  row.classList.add('has-error');
  field.setAttribute('aria-invalid', 'true');
  if (slot) slot.textContent = message;
}

function clearError(field) {
  const row = field.closest('.form__row');
  if (!row) return;
  row.classList.remove('has-error');
  field.removeAttribute('aria-invalid');
  const slot = row.querySelector('[data-error]');
  if (slot) slot.textContent = '';
}

function validate(field) {
  // Un select cuya primera opción es el placeholder no cuenta como elegido.
  if (field.tagName === 'SELECT' && field.required && field.selectedIndex === 0) {
    showError(field, 'Elegí una opción.');
    return false;
  }
  if (!field.checkValidity()) {
    showError(field, messageFor(field));
    return false;
  }
  clearError(field);
  return true;
}

/**
 * Orden y etiqueta de cada dato dentro del mail. La clave es el `name` del
 * campo en el HTML; el texto es el título que se lee en el mail recibido.
 */
const FIELDS = [
  ['nombre', 'Nombre completo'],
  ['whatsapp', 'WhatsApp'],
  ['email', 'Email'],
  ['instagram', 'Instagram de la empresa'],
  ['local', '¿Tiene local o showroom?'],
  ['tienda_online', '¿Tiene tienda online?'],
  ['url_tienda', 'URL de la tienda'],
  ['facturacion', 'Facturación mensual'],
];

const EMPTY = 'No completado';

function collect(form) {
  const data = new FormData(form);
  const out = {};
  FIELDS.forEach(([name, label]) => {
    out[label] = String(data.get(name) ?? '').trim() || EMPTY;
  });
  return out;
}

/**
 * Arma el cuerpo del mail. Web3Forms usa las claves del objeto como títulos
 * y respeta el orden en que se cargaron, así que el mail sale ordenado.
 */
function payload(form) {
  const data = collect(form);
  const nombre = data['Nombre completo'];
  const email = data['Email'];

  return {
    access_key: FORM.accessKey,
    subject: `Nueva solicitud de reunión — ${nombre !== EMPTY ? nombre : 'sin nombre'}`,
    from_name: FORM.fromName,
    // Responder el mail le contesta directo a la persona, no a Web3Forms.
    replyto: email !== EMPTY ? email : CONTACT.email,
    ...data,
  };
}

async function send(form) {
  const res = await fetch(FORM.endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload(form)),
  });

  // Web3Forms contesta 200 con { success: false } cuando rechaza el envío
  // (clave inválida, límite superado), así que no alcanza con mirar res.ok.
  const body = await res.json().catch(() => null);
  if (!res.ok || !body?.success) {
    throw new Error(body?.message || `HTTP ${res.status}`);
  }
}

export function init() {
  const form = document.querySelector('[data-form]');
  if (!form) return;

  if (!FORM.accessKey) {
    console.warn(
      '[form] Falta FORM.accessKey en js/config.js: el formulario no va a poder ' +
        'enviar el mail. Pedí la clave gratis en https://web3forms.com'
    );
  }

  const fields = Array.from(form.querySelectorAll('input, select, textarea')).filter(
    (f) => f.dataset.honeypot === undefined
  );
  const fieldset = form.querySelector('[data-form-fields]');
  const success = form.querySelector('[data-form-success]');
  const status = form.querySelector('[data-form-status]');
  const submit = form.querySelector('[type="submit"]');
  const honeypot = form.querySelector('[data-honeypot]');

  // La validación nativa se apaga para poder mostrar todos los errores
  // juntos y con nuestro texto; checkValidity() sigue haciendo el trabajo.
  form.setAttribute('novalidate', '');

  fields.forEach((field) => {
    // Primer aviso al salir del campo; después, corrección en vivo.
    field.addEventListener('blur', () => validate(field));
    field.addEventListener('input', () => {
      if (field.closest('.form__row')?.classList.contains('has-error')) validate(field);
    });
    field.addEventListener('change', () => {
      if (field.tagName === 'SELECT') validate(field);
    });
  });

  const setStatus = (text, isError = false) => {
    if (!status) return;
    status.textContent = text;
    status.classList.toggle('form__status--error', isError);
  };

  form.addEventListener('submit', async (e) => {
    e.preventDefault();

    // Trampa anti-spam: si vino cargado, es un bot. Fingimos éxito.
    if (honeypot && honeypot.value) {
      fieldset?.setAttribute('hidden', '');
      success?.classList.add('is-visible');
      return;
    }

    const invalid = fields.filter((f) => !validate(f));
    if (invalid.length) {
      setStatus(`Faltan ${invalid.length} dato(s) por completar.`, true);
      invalid[0].focus();
      return;
    }

    setStatus('');
    form.classList.add('is-sending');
    if (submit) submit.textContent = 'Enviando…';

    try {
      await send(form);

      fieldset?.setAttribute('hidden', '');
      success?.classList.add('is-visible');
      success?.focus();
    } catch (err) {
      console.error('[form] envío fallido:', err);
      setStatus(`No pudimos enviar el formulario. Escribinos a ${CONTACT.email}.`, true);
    } finally {
      form.classList.remove('is-sending');
      if (submit) submit.textContent = 'Agendar reunión';
    }
  });
}
